import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, CheckCircle2, User, Building2, Phone, ArrowLeft, Zap, Check } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const TestPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    company: '',
    email: '',
    phone: '',
    package: 'Basic TRIAL',
    message: ''
  });

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) return;
    
    setIsLoading(true);
    setErrorMessage('');

    try {
      const { data, error } = await supabase.functions.invoke('public-trial-registration', {
        body: formData,
      });

      // --- LOGIKA ZA HVATANJE SPECIFIČNIH GREŠAKA IZ EDGE FUNKCIJE ---
      if (error) {
        let errorMsg = 'Si è verificato un errore.';
        
        // Pokušaj izvući JSON grešku (poput EMAIL_EXISTS)
        try {
          const errDetails = await error.context.json();
          if (errDetails.error === 'EMAIL_EXISTS') {
            // Koristi prevod ako postoji, inače ispiši poruku na našem jeziku
            errorMsg = t('test_page.error_email_exists') || 'Ovaj email je već registrovan u bazi.';
          } else {
            errorMsg = errDetails.error || error.message;
          }
        } catch (e) {
          errorMsg = error.message || 'Errore di connessione.';
        }

        setErrorMessage(errorMsg);
        return; // Zaustavi dalji proces (nemoj prikazati success)
      }

      // Ako je sve OK
      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
        setFormData({ 
          name: '', 
          surname: '', 
          company: '', 
          email: '', 
          phone: '', 
          package: 'Basic TRIAL', 
          message: '' 
        });
        setAcceptedTerms(false);
      }, 5000);

    } catch (err) {
      console.error("Greška pri registraciji:", err);
      setErrorMessage(err.message || 'Si è verificato un errore.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        
        {/* Dugme za povratak */}
        <div className="relative z-50"> 
          <button 
            onClick={() => navigate(-1)} 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8 group cursor-pointer bg-transparent border-none p-0"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            {t('test_page.back')}
          </button>
        </div>

        {/* Naslovna sekcija */}
        <div className="text-center space-y-4 mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-full mb-4 shadow-inner">
            <Zap size={40} fill="currentColor" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter">
            {t('test_page.title')}
          </h1>
          <p className="text-muted-foreground text-sm uppercase tracking-[0.2em] font-bold">
            {t('test_page.subtitle')}
          </p>
        </div>

        {submitted ? (
          <div className="bg-card border-2 border-primary/20 p-12 rounded-[2.5rem] text-center space-y-6 shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="flex justify-center">
              <CheckCircle2 size={80} className="text-emerald-500 animate-pulse" />
            </div>
            <div className="space-y-3">
              <h2 className="text-3xl font-black uppercase italic">
                {t('test_page.success_title')}
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                {t('test_page.success_desc')}
              </p>
            </div>
            <div className="pt-4 text-[10px] font-black uppercase tracking-widest text-primary">
              {t('test_page.brand_subtitle')}
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="bg-primary/5 border border-primary/10 rounded-3xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-primary text-white p-3 rounded-2xl shadow-lg shadow-primary/20">
                  <Building2 size={24} />
                </div>
                <div className="text-left">
                  <h4 className="font-black uppercase text-sm">Basic</h4>
                  <p className="text-xs text-muted-foreground tracking-wide">
                    {t('test_page.basic_info')}
                  </p>
                </div>
              </div>
              <div className="px-4 py-2 bg-background rounded-full border border-primary/20 text-[10px] font-black text-primary uppercase tracking-widest">
                {t('test_page.gratis')}
              </div>
            </div>

            <section className="bg-card p-8 md:p-12 rounded-[2.5rem] border border-border shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  
                  {/* Ime */}
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2">
                      {t('test_page.label_name')}
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <input required type="text" name="name" value={formData.name} onChange={handleChange} placeholder={t('test_page.placeholder_name')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium" />
                    </div>
                  </div>

                  {/* Prezime */}
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2">
                      {t('test_page.label_surname') || 'COGNOME'}
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <input required type="text" name="surname" value={formData.surname} onChange={handleChange} placeholder={t('test_page.placeholder_surname') || 'Cognome'} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium" />
                    </div>
                  </div>

                  {/* Firma */}
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2">
                      {t('test_page.label_company')}
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <input required type="text" name="company" value={formData.company} onChange={handleChange} placeholder={t('test_page.placeholder_company')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium" />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2">
                      {t('test_page.label_email')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <input required type="email" name="email" value={formData.email} onChange={handleChange} placeholder={t('test_page.placeholder_email')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium" />
                    </div>
                  </div>

                  {/* Telefon */}
                  <div className="col-span-full space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2">
                      {t('test_page.label_phone')}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('test_page.placeholder_phone')} className="w-full bg-background border border-border rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium" />
                    </div>
                  </div>
                </div>

                {/* Poruka/Napomena */}
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground ml-2">
                    {t('test_page.label_note')}
                  </label>
                  <textarea name="message" rows="4" value={formData.message} onChange={handleChange} placeholder={t('test_page.placeholder_note')} className="w-full bg-background border border-border rounded-2xl py-5 px-6 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none font-medium"></textarea>
                </div>

                {/* Checkbox za uslove */}
                <div className="flex items-start gap-3 px-2 py-2">
                  <div className="relative flex items-center justify-center shrink-0">
                    <input 
                      type="checkbox"
                      id="terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="peer h-6 w-6 cursor-pointer appearance-none rounded-lg border-2 border-border bg-background transition-all checked:border-primary checked:bg-primary"
                    />
                    <Check className="absolute h-4 w-4 text-white opacity-0 transition-opacity peer-checked:opacity-100 pointer-events-none" strokeWidth={4} />
                  </div>
                  <label htmlFor="terms" className="text-sm text-muted-foreground font-medium cursor-pointer select-none pt-0.5">
                    {t('test_page.terms_part1')}{' '}
                    <Link to="/legal-page" className="text-primary font-bold hover:underline underline-offset-4 transition-all">
                      {t('test_page.terms_link')}
                    </Link>{' '}
                    {t('test_page.terms_part2')}
                  </label>
                </div>

                {/* PRIKAZ GREŠKE */}
                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-xs font-bold text-center animate-shake">
                    {errorMessage}
                  </div>
                )}

                {/* Submit dugme */}
                <button 
                  type="submit"
                  disabled={!acceptedTerms || isLoading}
                  className={`w-full font-black uppercase tracking-[0.1em] py-5 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl text-sm ${
                    acceptedTerms && !isLoading
                    ? 'bg-primary text-white hover:brightness-110 active:scale-[0.98] shadow-primary/25 cursor-pointer' 
                    : 'bg-muted text-muted-foreground cursor-not-allowed opacity-70 shadow-none'
                  }`}
                >
                  {isLoading ? 'Processing...' : t('test_page.btn_send')}
                  {!isLoading && <Send size={18} />}
                </button>
              </form>
            </section>
          </div>
        )}

        <div className="mt-12 text-center border-t border-border pt-8">
           <p className="text-xs text-muted-foreground font-medium italic">
             {t('test_page.footer_note')}
           </p>
        </div>
      </div>
    </div>
  );
};

export default TestPage;